import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Input, Space, Row, Col, Statistic,
    Modal, Form, message, Select, Typography, Popconfirm
} from 'antd';
import {
    SearchOutlined, PlusOutlined, UserOutlined, TeamOutlined,
    UsergroupAddOutlined, CheckCircleOutlined, ClockCircleOutlined, StopOutlined,
    EyeOutlined, CopyOutlined, FlagOutlined, DeleteOutlined, EditOutlined
} from '@ant-design/icons';
import ambassadorsService from '../../common/services/ambassadors.service';
import citiesService from '../../common/services/cities.service';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const AmbassadorsPage = () => {
    const navigate = useNavigate();

    // State
    const [loading, setLoading] = useState(false);
    const [ambassadors, setAmbassadors] = useState([]);
    const [stats, setStats] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [filters, setFilters] = useState({ search: '', status: '' });
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createForm] = Form.useForm();
    const [creating, setCreating] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editForm] = Form.useForm();
    const [editing, setEditing] = useState(false);
    const [editingAmbassador, setEditingAmbassador] = useState(null);
    const [cities, setCities] = useState([]);

    // Fetch ambassadors
    const fetchAmbassadors = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await ambassadorsService.getAmbassadors(params);
            if (response.success) {
                setAmbassadors(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error('Erreur lors du chargement des ambassadeurs');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    // Fetch stats
    const fetchStats = async () => {
        try {
            const response = await ambassadorsService.getStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Erreur stats:', error);
        }
    };

    useEffect(() => {
        fetchAmbassadors();
    }, [fetchAmbassadors]);

    useEffect(() => {
        fetchStats();
        fetchCities();
    }, []);

    const fetchCities = async () => {
        try {
            const response = await citiesService.getAll();
            if (response.success) {
                setCities(response.data || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    };

    // Handlers
    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (value) => {
        setFilters(prev => ({ ...prev, status: value || '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleTableChange = (paginationConfig) => {
        setPagination(prev => ({
            ...prev,
            page: paginationConfig.current,
            limit: paginationConfig.pageSize
        }));
    };

    const handleCreate = async (values) => {
        setCreating(true);
        try {
            const response = await ambassadorsService.createAmbassador(values);
            if (response.success) {
                message.success('Ambassadeur cree avec succes');
                setCreateModalVisible(false);
                createForm.resetFields();
                fetchAmbassadors();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de la creation');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error(error.message || 'Erreur lors de la creation');
        } finally {
            setCreating(false);
        }
    };

    const handleEdit = (record) => {
        setEditingAmbassador(record);
        editForm.setFieldsValue({
            firstName: record.firstName,
            lastName: record.lastName,
            phone: record.phone,
            email: record.email,
            city: record.city?._id
        });
        setEditModalVisible(true);
    };

    const handleUpdate = async (values) => {
        setEditing(true);
        try {
            const response = await ambassadorsService.updateAmbassador(editingAmbassador._id, values);
            if (response.success) {
                message.success('Ambassadeur modifié avec succès');
                setEditModalVisible(false);
                editForm.resetFields();
                setEditingAmbassador(null);
                fetchAmbassadors();
            } else {
                message.error(response.message || 'Erreur lors de la modification');
            }
        } catch (error) {
            console.error('Erreur modification:', error);
            message.error(error.message || 'Erreur lors de la modification');
        } finally {
            setEditing(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        message.success('Code copie !');
    };

    const handleDelete = async (id) => {
        try {
            const response = await ambassadorsService.deleteAmbassador(id);
            if (response.success) {
                message.success('Ambassadeur supprime avec succes');
                fetchAmbassadors();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur:', error);
            message.error(error.message || 'Erreur lors de la suppression');
        }
    };

    // Columns
    const columns = [
        {
            title: 'Ambassadeur',
            key: 'name',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.phone}</div>
                    </div>
                </Space>
            )
        },
        {
            title: 'Reseau',
            key: 'network',
            render: (_, record) => {
                const stats = record.ambassadorProfile?.stats || {};
                return (
                    <Space>
                        <Tag icon={<UsergroupAddOutlined />} color="geekblue">
                            {stats.totalAffiliates || 0} affilies
                        </Tag>
                        <Tag icon={<UserOutlined />} color="cyan">
                            {stats.totalDrivers || 0} chauffeurs
                        </Tag>
                    </Space>
                );
            }
        },
        {
            title: 'Ville',
            key: 'city',
            render: (_, record) => (
                <span>{record.city?.name || record.ambassadorProfile?.city || '-'}</span>
            )
        },
        {
            title: 'Statut',
            key: 'status',
            render: (_, record) => {
                const status = record.ambassadorProfile?.status;
                const statusConfig = {
                    approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Actif' },
                    pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
                    suspended: { color: 'error', icon: <StopOutlined />, text: 'Suspendu' }
                };
                const config = statusConfig[status] || statusConfig.pending;
                return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
            }
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, record) => (
                <span style={{ fontSize: '12px' }}>
                    {dayjs(record.ambassadorProfile?.approvedAt || record.createdAt).format('DD/MM/YYYY')}
                </span>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/dashboard/ambassadors/${record._id}`)}
                    >
                        Detail
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        style={{ color: '#faad14', borderColor: '#faad14' }}
                    >
                        Modifier
                    </Button>
                    <Popconfirm
                        title="Supprimer cet ambassadeur ?"
                        description="Cette action est irreversible."
                        onConfirm={() => handleDelete(record._id)}
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            Supprimer
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="ambassadors-page" style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <Title level={3} style={{ margin: 0 }}>
                    <FlagOutlined style={{ marginRight: '12px' }} />
                    Programme Brand Ambassador
                </Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                >
                    Nouvel Ambassadeur
                </Button>
            </div>

            {/* Stats */}
            {stats && (
                <Row gutter={16} style={{ marginBottom: '24px' }}>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Ambassadeurs"
                                value={stats.totalAmbassadors || 0}
                                prefix={<TeamOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Actifs"
                                value={stats.approvedAmbassadors || 0}
                                valueStyle={{ color: '#52c41a' }}
                                prefix={<CheckCircleOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Total Affilies"
                                value={stats.totalAffiliates || 0}
                                prefix={<UsergroupAddOutlined />}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card>
                            <Statistic
                                title="Candidatures en attente"
                                value={stats.pendingApplications || 0}
                                valueStyle={{ color: stats.pendingApplications > 0 ? '#faad14' : undefined }}
                                prefix={<ClockCircleOutlined />}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Filters */}
            <Card style={{ marginBottom: '24px' }}>
                <Space wrap>
                    <Input.Search
                        placeholder="Rechercher par nom, telephone, code..."
                        allowClear
                        onSearch={handleSearch}
                        style={{ width: 300 }}
                        prefix={<SearchOutlined />}
                    />
                    <Select
                        placeholder="Statut"
                        allowClear
                        style={{ width: 150 }}
                        onChange={handleStatusFilter}
                        value={filters.status || undefined}
                    >
                        <Option value="approved">Actif</Option>
                        <Option value="pending">En attente</Option>
                        <Option value="suspended">Suspendu</Option>
                    </Select>
                </Space>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={ambassadors}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `Total: ${total}`
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            {/* Modal Creation */}
            <Modal
                title="Creer un nouvel ambassadeur"
                open={createModalVisible}
                onCancel={() => {
                    setCreateModalVisible(false);
                    createForm.resetFields();
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="firstName"
                                label="Prenom"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Input placeholder="Prenom" style={{ textTransform: 'uppercase' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lastName"
                                label="Nom"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Input placeholder="Nom" style={{ textTransform: 'uppercase' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Telephone"
                                rules={[
                                    { required: true, message: 'Requis' },
                                    { pattern: /^[0-9]{10}$/, message: '10 chiffres requis' }
                                ]}
                            >
                                <Input placeholder="Ex: 0700000000" maxLength={10} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[{ type: 'email', message: 'Email invalide' }]}
                            >
                                <Input placeholder="Email (optionnel)" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'Ville requise' }]}
                    >
                        <Select placeholder="Sélectionnez une ville" allowClear>
                            {cities.filter(c => c.isActive).map(city => (
                                <Option key={city._id} value={city._id}>{city.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Mot de passe"
                        extra="Laissez vide pour le mot de passe par defaut (123456)"
                    >
                        <Input.Password placeholder="Mot de passe (optionnel)" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setCreateModalVisible(false)}>
                                Annuler
                            </Button>
                            <Button type="primary" htmlType="submit" loading={creating}>
                                Creer
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal Modification */}
            <Modal
                title="Modifier l'ambassadeur"
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    editForm.resetFields();
                    setEditingAmbassador(null);
                }}
                footer={null}
                width={600}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdate}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="firstName"
                                label="Prénom"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lastName"
                                label="Nom"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Téléphone"
                                rules={[{ required: true, message: 'Requis' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label="Email"
                            >
                                <Input type="email" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'Ville requise' }]}
                    >
                        <Select placeholder="Sélectionnez une ville" allowClear>
                            {cities.filter(c => c.isActive).map(city => (
                                <Option key={city._id} value={city._id}>{city.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => {
                                setEditModalVisible(false);
                                editForm.resetFields();
                                setEditingAmbassador(null);
                            }}>
                                Annuler
                            </Button>
                            <Button type="primary" htmlType="submit" loading={editing}>
                                Enregistrer
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AmbassadorsPage;
